// ---------------------------------------------------------------------------
// G.19: Breakout Rooms — Participant Views (Self-Select + Assigned)
// ---------------------------------------------------------------------------

import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { BreakoutRoom } from "./breakout-types";
import { ACTIVITY_COLORS, formatTime } from "./breakout-types";

// ---------------------------------------------------------------------------
// Self-Select View
// ---------------------------------------------------------------------------

interface ParticipantSelfSelectViewProps {
  webinarId: string;
  breakoutId: string;
  rooms: BreakoutRoom[];
  timeRemainingSeconds: number | null;
  warningMinutesBefore: number;
  broadcasts: Array<{ message: string; sentAt: string }>;
}

export function ParticipantSelfSelectView({
  webinarId,
  breakoutId,
  rooms,
  timeRemainingSeconds,
  warningMinutesBefore,
  broadcasts,
}: ParticipantSelfSelectViewProps) {
  const [joinedRoomId, setJoinedRoomId] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const isWarning = timeRemainingSeconds !== null && timeRemainingSeconds <= warningMinutesBefore * 60;

  const handleJoinRoom = async (roomId: string) => {
    setIsJoining(true);
    try {
      await apiFetch<{ data: { livekitRoom: string } }>(
        `/v1/webinars/${webinarId}/breakout/${breakoutId}/visit/${roomId}`,
        { method: "POST" },
      );
      setJoinedRoomId(roomId);
    } catch {
      // Error handled by apiFetch
    } finally {
      setIsJoining(false);
    }
  };

  const joinedRoom = rooms.find((r) => r.id === joinedRoomId);
  const latestBroadcast = broadcasts.length > 0 ? broadcasts[broadcasts.length - 1] : null;

  return (
    <div className="space-y-4">
      {/* Timer */}
      <TimerBanner
        timeRemainingSeconds={timeRemainingSeconds}
        isWarning={isWarning}
        label={isWarning ? "Breakout ending soon" : "Breakout session in progress"}
      />

      {/* Latest broadcast */}
      {latestBroadcast && <BroadcastBanner message={latestBroadcast.message} />}

      {/* Joined Room */}
      {joinedRoom && (
        <div className="rounded-lg border-2 border-primary bg-card p-4">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Current Room</span>
          </div>
          <h3 className="text-base font-semibold text-foreground">{joinedRoom.name}</h3>
          {joinedRoom.topic && (
            <p className="mt-1 text-sm text-muted-foreground">{joinedRoom.topic}</p>
          )}
          <p className="mt-2 text-sm text-muted-foreground">{joinedRoom.participantCount} participants</p>
        </div>
      )}

      {/* Room Selection */}
      {!joinedRoomId && (
        <>
          <h2 className="text-base font-semibold text-foreground">Choose a Room to Join</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {rooms.map((room) => (
              <div key={room.id} className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-1 text-sm font-semibold text-foreground">{room.name}</h3>
                {room.topic && (
                  <p className="mb-2 text-xs text-muted-foreground line-clamp-2">{room.topic}</p>
                )}
                <div className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <span className={`h-2 w-2 rounded-full ${ACTIVITY_COLORS[room.activityLevel]}`} />
                  {room.participantCount} participants
                </div>
                <button
                  onClick={() => handleJoinRoom(room.id)}
                  disabled={isJoining}
                  className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  Join
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assigned View (random / manual)
// ---------------------------------------------------------------------------

interface ParticipantAssignedViewProps {
  rooms: BreakoutRoom[];
  timeRemainingSeconds: number | null;
  warningMinutesBefore: number;
  broadcasts: Array<{ message: string; sentAt: string }>;
}

export function ParticipantAssignedView({
  rooms,
  timeRemainingSeconds,
  warningMinutesBefore,
  broadcasts,
}: ParticipantAssignedViewProps) {
  const isWarning = timeRemainingSeconds !== null && timeRemainingSeconds <= warningMinutesBefore * 60;
  const latestBroadcast = broadcasts.length > 0 ? broadcasts[broadcasts.length - 1] : null;

  return (
    <div className="space-y-4">
      {/* Timer */}
      <TimerBanner
        timeRemainingSeconds={timeRemainingSeconds}
        isWarning={isWarning}
        label={isWarning ? "Returning to main room soon" : "Breakout session in progress"}
      />

      {/* Latest broadcast */}
      {latestBroadcast && <BroadcastBanner message={latestBroadcast.message} />}

      {/* Assignment notice */}
      <div className="rounded-lg border-2 border-primary bg-card p-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-foreground">You have been assigned to a breakout room</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          You will be automatically moved to your assigned room.
        </p>
      </div>

      {/* All rooms overview */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium text-foreground">All Rooms</h3>
        <div className="space-y-2">
          {rooms.map((room) => (
            <div key={room.id} className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
              <div>
                <span className="text-sm font-medium text-foreground">{room.name}</span>
                {room.topic && (
                  <span className="ml-2 text-xs text-muted-foreground">{room.topic}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${ACTIVITY_COLORS[room.activityLevel]}`} />
                <span className="text-xs text-muted-foreground">{room.participantCount}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function TimerBanner({
  timeRemainingSeconds,
  isWarning,
  label,
}: {
  timeRemainingSeconds: number | null;
  isWarning: boolean;
  label: string;
}) {
  if (timeRemainingSeconds === null) return null;

  return (
    <div className={`flex items-center justify-between rounded-lg border border-border bg-card p-3 ${isWarning ? "border-yellow-300 bg-[var(--color-warning)]/5" : ""}`}>
      <span className="text-sm text-foreground">{label}</span>
      <span className={`text-lg font-bold tabular-nums ${isWarning ? "text-[var(--color-destructive)]" : "text-foreground"}`}>
        {formatTime(timeRemainingSeconds)}
      </span>
    </div>
  );
}

function BroadcastBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-4 py-2 text-sm text-[var(--color-primary)]">
      <span className="font-medium">Host: </span>{message}
    </div>
  );
}
